<template>
    <tbody>
        <template v-if="!componentIsLoading">
            <item-summary 
                ref="itemSummary"
                :document-name="expectedDocument.document_name"
                :document-status="documentStatus"
                :template-link="displayTemplateLink ? templateLink : null"
                @toggle-details="showDetails = !showDetails" 
            ></item-summary>

            <item-detail 
                v-if="showDetails"
                ref="itemDetail"
                :index="index"
                :expected-document="expectedDocument"
                @expiration-date-updated="onExpirationDateUpdated($event)"
                @file-uploaded="onFileUploaded($event)"
                @file-deleted="onFileDeleted($event)"
                @document-validated="onDocumentValidated($event)"
                @document-refused="onDocumentRefused($event)"
            ></item-detail>
        </template>

        <tr v-else>
            <td colspan="3">
                <component-loader :min-height="'50px'"></component-loader>
            </td>
        </tr>
    </tbody>
</template>

<script type="text/babel">
import ItemSummary from './ItemSummary'
import ItemDetail from './ItemDetail'
import ComponentLoader from 'components/ComponentLoader'
import api from 'store/api'
import cookies from "cookiesjs"

export default {
    name:"DocumentItem",
    
    props: {
        index: {
            type: Number,
            required: true
        },
        customerId: {
            type: Number,
            required: true
        },
        expectedDocument: {
            type: Object,
            required: true
        }
    },
    
    data () {
        return {
            componentIsLoading: false,
            contract: null,
            showDetails: false
        }
    },

    components: {
        ComponentLoader,
        ItemSummary,
        ItemDetail
    },

    mounted() {
        if (this.isContract) {
            this.syncContractData()
        }
    },

    computed: {
        documentType() {
            return this.expectedDocument.document_type
        },
        hasDifferentVersionIds() {
            return this.expectedDocument.draft_version !== null && this.expectedDocument.current_version.id != this.expectedDocument.draft_version.id
        },
        customerDocument() {
            return this.hasDifferentVersionIds && this.expectedDocument.draft_version.has_files
                ? this.expectedDocument.draft_version
                : this.expectedDocument.current_version
        },
        customerDocumentIsEditable() {
            return this.expectedDocument.draft_version && 
                (!this.customerDocument || (this.customerDocument && !['pending-validation'].includes(this.customerDocument.status))) &&
                (!this.hasDifferentVersionIds || (this.hasDifferentVersionIds && ['all', 'agency'].includes(this.documentType.versioning_type)))
        },
        isContract() {
            return ['domiciliation-contract', 'office-rental-contract'].includes(this.documentType.name)
        },
        contractId() {
            return this.isContract ? this.expectedDocument.owner.id : null
        },
        hasDigitalContract() {
            return this.isContract && this.contract != null && this.contract.digital_contract != null
        },
        isDigitallySignableContract() {
            return this.documentInProgress && this.isContract && this.contract != null && this.contract.is_digitally_signable
        },
        templateLink() {
          return this.documentType.has_template 
            ? __API_URL__ + '/customer_documents/' + this.customerDocument.id + '/get_template?token=' + cookies("token")
            : null
        },
        displayTemplateLink() {
            // return this.templateLink && this.customerDocumentIsEditable && (!this.isContract || this.isDigitallySignableContract)
            return this.templateLink && this.customerDocumentIsEditable
        },
        documentStatusClass() {
            if ((!this.customerDocument || ('in-progress' == this.customerDocument.status && !this.customerDocument.has_files)) && !this.digitalSignInProgress) {
                return ''
            }

            if (this.digitalSignInProgress) {
                return 'pending-validation'
            }

            if (this.customerDocument.expired) {
                return 'expired'
            }

            return this.customerDocument.status
        },
        documentInProgress() {
            return !this.customerDocument || 'in-progress' == this.customerDocument.status || this.customerDocument.expired
        },
        documentStatusIconHref() {
            if (this.digitalSignInProgress) {
                return '#pending-validation-circle-icon'
            }

            if (this.documentInProgress) {
                return '#in-progress-circle-icon'
            }

            return `#${this.customerDocument.status}-circle-icon`
        },
        digitalSignInProgress() {
            return this.hasDigitalContract && 'in_progress' == this.contract.digital_contract.state
        },
        digitalSignInProgressStateText() {
            if (!this.digitalSignInProgress) {
                return ''
            }

            const digitalContract = this.contract.digital_contract

            if (digitalContract && digitalContract.signatories.length > 0 && digitalContract.signatories[0].pivot && digitalContract.signatories[0].pivot.signed_at !== null) {
                return digitalContract.signed_by_sofradom ? 'Contrat signé' : 'En attente de validation par Sofradom'
            } else {
                return 'En attente de signature client'
            }
        },
        documentExpirationDate() {
            return this.customerDocument.expired_at 
                ? this.$options.filters['moment-date-compact'](this.customerDocument.expired_at)
                : ''
        },
        documentStateText() {
            if (!this.customerDocument || 'in-progress' == this.customerDocument.status) {
                return !this.customerDocument || !this.customerDocument.has_files ? 'À fournir' : 'À compléter et valider'
            }

            if ('pending-validation' == this.customerDocument.status) {
                return 'En attente de validation par Sofradom'
            }

            if (this.documentType.can_expire) {
                if (this.customerDocument.expired) {
                    return 'À fournir <span class="expiration-date" style="font-weight: 600; margin-left: 10px;">Expiré le ' + this.documentExpirationDate + '</span>'
                }

                return 'Expire le ' + this.documentExpirationDate
            }

            return 'Validé'
        },
        documentStatus() {
            return {
                class: this.documentStatusClass,
                iconHref: this.documentStatusIconHref,
                html: this.digitalSignInProgress ? this.digitalSignInProgressStateText : this.documentStateText
            }
        }
    },

    methods: {
        fetchContract() {
            if (!this.isContract || this.contractId == null) {
                return
            }

            this.componentIsLoading = true

            return api.getCustomerContract(this.customerId, this.contractId, {with :'digital_contract.signatories;digital_contracts'})
                .then(response => {
                    this.contract = response.data.contract
                })
                .then(() => this.componentIsLoading = false)
        },
        syncContractData() {
            if (!this.isContract || this.contractId == null) {
                return
            }

            this.componentIsLoading = true

            return api.syncCustomerDigitalContracts(this.customerId, this.contractId)
                .then(response => {
                    this.fetchContract()
                })
                .catch(error => {
                    this.componentIsLoading = false
                })
        },
        onExpirationDateUpdated(index) {
            this.$emit('expiration-date-updated', index)
        },
        onFileUploaded(index) {
            this.$emit('file-uploaded', index)
        },
        onFileDeleted(index) {
            this.$emit('file-deleted', index)
        },
        onDocumentValidated(index) {
            this.$emit('document-validated', index)
        },
        onDocumentRefused(index) {
            this.$emit('document-refused', index)
        }
    }
}
</script>

<style lang="scss" scoped>
    tbody + tbody {
        border-top: inherit;
    }
</style>


// WEBPACK FOOTER //
// client/components/CustomerDocuments/Item.vue